import type { WaTextarea } from "@web.awesome.me/webawesome-pro/dist/ssr/all.js";
import DomPurify from "dompurify";
import { html, type PropertyValues } from "lit";
import { live } from "lit/directives/live.js";
import { marked } from "marked";
import { z } from "zod";
import { checkNotNil } from "../../check-not-nil";
import { FormAssociationMixin } from "../../mixins/form-associated-element";
import { attributeProperty } from "../../property.js";
import type { ParserType } from "../../schemas/parser";
import { WmElement } from "../element";

type Value = ParserType<typeof Value>;
const Value = attributeProperty("value", z.string());

type ValueError = ParserType<typeof ValueError>;
const ValueError = attributeProperty("error", z.string());

/**
A form control specifically designed for a note's content field.
*/
export class WmNoteContent extends FormAssociationMixin(WmElement) {
  static readonly TAG_NAME = "wm-note-content";

  static override properties = {
    ...super.properties,
    value: Value,
    error: ValueError,
  };

  declare value: Value;
  declare error: ValueError;

  constructor() {
    super();
    this.name = "content";
    this.value = undefined;
    this.error = undefined;
  }

  // TODO: freestanding function? Kind of sucks that the element has to be
  // created for that esééééé.
  /**
  Computes a title based on the element's value.

  The computed title is the first non empty line of text, after markdown
  parsing and DOM sanitization, truncated to fit within 256 UTF-8 codepoints.
  */
  get computedTitle() {
    if (this.value == null) {
      return "";
    }
    const html = marked.parse(this.value, { async: false });
    const sanitized = DomPurify.sanitize(html, {
      USE_PROFILES: { html: true },
    });
    const div = document.createElement("div");
    div.innerHTML = sanitized;
    const text = div.innerText;
    // Find first non empty line.
    const firstNonEmptyLine =
      text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .find((l) => l !== "") ?? "";
    const chars = [...firstNonEmptyLine];
    // Strip if it goes over 256 UTF-8 code points.
    if (chars.length > 256) {
      return `${chars.slice(0, 254).join("")}...`;
    }
    return firstNonEmptyLine;
  }

  protected override render() {
    return html`
        <wa-textarea
          id="content"
          label="Content"
          maxlength="4096"
          name="content"
          .value="${live(this.value ?? "")}"
          with-count
          resize="auto"
          @input=${this.#contentInput}>
          <span slot="hint">
            Notes support
            <a href="https://www.markdownguide.org/basic-syntax/" target="_blank">
              Markdown<wa-icon name="arrow-up-right-from-square"></wa-icon>
            </a>
          </span>
        </wa-textarea>`;
  }

  protected override updated(_: PropertyValues<this>): void {
    const textarea = this.#queryTextarea();
    textarea.updateComplete.then(() => {
      // The validation target is only available after render, of course.
      // This is true here and for Web Awesome components.
      const validationTarget = checkNotNil(textarea.validationTarget);
      // Pre trimmed for our friend the backend. Although it will trim it
      // regardless.
      this.internals.setFormValue((this.value ?? "").trim());
      // If the user has provided a custom error, then this takes precedence
      // and immediately triggers a validity report.
      if (this.error) {
        this.internals.setValidity(
          { customError: true },
          this.error,
          validationTarget,
        );
        this.reportValidity();
      } else {
        // Otherwise, we simply delegate to the textarea. WA's textarea does
        // not set its initial validity correctly before receiving input
        // events. Indeed, even when filled with a value, the text area reports
        // as invalid (as if it had no content). Therefore, we manually trigger
        // the update at the time we need it.
        textarea.updateValidity();
        this.internals.setValidity(
          textarea.validity,
          textarea.validationMessage,
          validationTarget,
        );
      }
    });
  }

  #contentInput(event: InputEvent) {
    const target = checkNotNil(event.target) as WaTextarea;
    this.value = target.value;
    this.error = undefined;
  }

  #queryTextarea() {
    return checkNotNil(this.renderRoot.querySelector("wa-textarea"));
  }
}

customElements.define(WmNoteContent.TAG_NAME, WmNoteContent);

declare global {
  interface HTMLElementTagNameMap {
    [WmNoteContent.TAG_NAME]: WmNoteContent;
  }
}
